import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  CoreCounterState,
  CoreCountersActions,
  DataViewUIConfigurationModel,
  EntityActions,
  EntityState,
  EntryActions,
  ViewActions,
  ViewState,
  getViewGeneralStyleId,
} from '@osos/shared/legacy-core';
import {
  CounterUserEntryPresenterModel,
  CounterWidgetDataEntryPresenterModel,
  DATA_VIEW_QUERY_PARAM,
  DEFAULT_PAGESIZE,
  DataEntityPresenterModel,
  DataEntryPresenterModel,
  DataQueryPresenterModel,
  DataViewsPresenterModel,
  ExpressionTypeEnum,
  FilterExpressionPresenterModel,
  FilterExpressionValueTypeEnum,
} from '@osos/shared/types';
import { ComponentBase } from '@osos/shared/utility';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, takeUntil,  } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-notification-counter-menu',
  templateUrl: './notification-counter-menu.component.html',
  styleUrls: ['./notification-counter-menu.component.scss'],
  standalone: false,
})
export class NotificationCounterMenuComponent
  extends ComponentBase
  implements OnInit
{
  @Output() closeMenu: EventEmitter<any> = new EventEmitter();
  @Input() counter: CounterWidgetDataEntryPresenterModel;
  @Input() entriesCount: number;
  configuration: DataViewUIConfigurationModel;
  entries$: Observable<CounterUserEntryPresenterModel[]>;
  selectedEntity: DataEntityPresenterModel;
  private _queryApplySubject: Subject<DataQueryPresenterModel> = new Subject();
  entriesLoading$ = new BehaviorSubject<boolean>(true);
  private _fetchEntriesSub = new BehaviorSubject<{
    skip: number;
    limit?: number;
  }>({ skip: 0, limit: DEFAULT_PAGESIZE });

  protected generalWrapperClass = getViewGeneralStyleId();

  constructor(
    private store: Store,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.entries$ = combineLatest([
      this._fetchDataViewAndEntity(
        this.counter.view.dataEntry,
        this.counter.viewDataEntity,
      ),
      this._fetchEntriesSub,
    ]).pipe(
      tap(() => this.entriesLoading$.next(true)),
      switchMap(([{ dataView, dataEntity }, { skip, limit }]) => {
        if (!this.configuration) {
          this._initConfiguration(dataView, dataEntity);
        }

        return this._fetchEntries(skip, limit);
      }),
      tap(() => this.entriesLoading$.next(false)),
    );
    this._queryApplySubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((applyQuery) => {
        this.router
          .navigate(['/v/', this.counter.view.dataEntry], {
            queryParams: { [DATA_VIEW_QUERY_PARAM]: 1 },
          })
          .then(() => {
            // Only dispatch this action once navigation has succeeded.
            this.store.dispatch(
              new EntryActions.SetPendingDataQueryForView({
                dataViewId: this.counter.view.dataEntry,
                dataQuery: applyQuery,
              }),
            );
          });
      });
  }
  onScroll(event: number) {
    this._fetchEntriesSub.next({ skip: event, limit: DEFAULT_PAGESIZE });
  }
  onClickItem(item: DataEntryPresenterModel) {
    const criteria: FilterExpressionPresenterModel = {
      args: [
        {
          operator: ExpressionTypeEnum.In,
          field: 'id',
          args: [
            {
              type: FilterExpressionValueTypeEnum.Fixed,
              value: [item.id],
            },
          ],
        },
      ],
      operator: ExpressionTypeEnum.And,
    };
    this._queryApplySubject.next({
      criteria: criteria,
    } as DataQueryPresenterModel);
    this.closeMenu.emit(true);
  }

  private _fetchDataViewAndEntity(
    viewId: string,
    entityId: string,
  ): Observable<{
    dataView: DataViewsPresenterModel;
    dataEntity: DataEntityPresenterModel;
  }> {
    return forkJoin([
      this.getDataView(viewId),
      this.getDataEntity(entityId, viewId),
    ]).pipe(
      map(([dataView, dataEntity]) => {
        return {
          dataView,
          dataEntity,
        };
      }),
    );
  }

  private _fetchEntries(
    skip: number,
    limit: number,
  ): Observable<DataEntryPresenterModel[]> {
    return this.store
      .dispatch(
        new CoreCountersActions.FetchCountersEntries({
          counterId: this.counter.id,
          viewId: this.counter.view.dataEntry,
          skip,
          limit,
        }),
      )
      .pipe(
        switchMap(() =>
          this.store.selectOnce(
            CoreCounterState.presentedCounterEntries(this.counter.id),
          ),
        ),
      );
  }
  private _initConfiguration(
    config: DataViewsPresenterModel,
    dataEntity: DataEntityPresenterModel,
  ): void {
    this.selectedEntity = dataEntity;
    this.configuration = new DataViewUIConfigurationModel(config, dataEntity);
  }

  getDataView(viewId): Observable<DataViewsPresenterModel> {
    return this.store
      .dispatch(new ViewActions.FetchViewForEndUser({ viewId }))
      .pipe(
        switchMap(() =>
          this.store.selectOnce(ViewState.presentedViewForEndUser(viewId)),
        ),
      );
  }

  getDataEntity(
    entityId: string,
    viewId: string,
  ): Observable<DataEntityPresenterModel> {
    return this.store
      .dispatch(new EntityActions.FetchEntity(entityId, viewId))
      .pipe(
        switchMap(() =>
          this.store.selectOnce(EntityState.presentedEntity(entityId)),
        ),
      );
  }
}
